import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Pencil, Trash2, Check, X } from "lucide-react";
import { apiFetch } from "@/lib/api";

interface Category {
  id: number;
  name: string;
  slug: string;
}

export default function Categories() {
  const queryClient = useQueryClient();
  const [newName, setNewName] = useState("");
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState("");
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const { data: categories = [] } = useQuery<Category[]>({
    queryKey: ["admin-categories"],
    queryFn: () => apiFetch<Category[]>("/api/admin/categories"),
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ["admin-categories"] });

  const createMutation = useMutation({
    mutationFn: (name: string) =>
      apiFetch("/api/admin/categories", {
        method: "POST",
        body: JSON.stringify({ name }),
      }),
    onSuccess: () => {
      invalidate();
      setNewName("");
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, name }: { id: number; name: string }) =>
      apiFetch(`/api/admin/categories/${id}`, {
        method: "PUT",
        body: JSON.stringify({ name }),
      }),
    onSuccess: () => {
      invalidate();
      setEditingId(null);
      setEditName("");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) =>
      apiFetch(`/api/admin/categories/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      invalidate();
      setDeleteError(null);
    },
    onError: (error: Error) => {
      setDeleteError(error.message);
    },
  });

  const handleAdd = () => {
    const name = newName.trim();
    if (!name) return;
    createMutation.mutate(name);
  };

  const startEdit = (category: Category) => {
    setEditingId(category.id);
    setEditName(category.name);
  };

  const handleSave = (id: number) => {
    const name = editName.trim();
    if (!name) return;
    updateMutation.mutate({ id, name });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditName("");
  };

  const handleDelete = (category: Category) => {
    if (!confirm(`Obrisati kategoriju "${category.name}"?`)) return;
    setDeleteError(null);
    deleteMutation.mutate(category.id);
  };

  return (
    <div className="max-w-lg mx-auto flex flex-col gap-4 sm:gap-6">
      <h1 className="text-xl sm:text-2xl font-bold">Kategorije</h1>

      {/* Add new */}
      <div className="flex gap-2">
        <Input
          placeholder="Naziv nove kategorije"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleAdd();
          }}
        />
        <Button onClick={handleAdd} disabled={createMutation.isPending}>
          <Plus className="w-4 h-4 mr-1" />
          Dodaj
        </Button>
      </div>

      {/* List */}
      <div className="flex flex-col gap-2">
        {categories.length === 0 && (
          <p className="text-sm text-muted-foreground">Nema kategorija.</p>
        )}
        {categories.map((category) => (
          <div
            key={category.id}
            className="flex items-center gap-2 border rounded-md px-3 py-2"
          >
            {editingId === category.id ? (
              <>
                <Input
                  className="flex-1"
                  value={editName}
                  autoFocus
                  onChange={(e) => setEditName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSave(category.id);
                    if (e.key === "Escape") handleCancelEdit();
                  }}
                />
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => handleSave(category.id)}
                  disabled={updateMutation.isPending}
                >
                  <Check className="w-4 h-4" />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={handleCancelEdit}
                >
                  <X className="w-4 h-4" />
                </Button>
              </>
            ) : (
              <>
                <span className="flex-1 font-medium">{category.name}</span>
                <span className="text-xs text-muted-foreground">
                  {category.slug}
                </span>
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => startEdit(category)}
                >
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => handleDelete(category)}
                  disabled={deleteMutation.isPending}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </>
            )}
          </div>
        ))}
      </div>

      {deleteError && (
        <p className="text-sm text-destructive">
          {deleteError === "cannot delete category with products"
            ? "Nije moguće obrisati kategoriju koja sadrži proizvode."
            : deleteError}
        </p>
      )}
    </div>
  );
}
